import { Button, Form, FormInstance, message, Modal, Spin, Steps, Tag, Typography } from "antd";
import { useEffect, useMemo, useRef, useState } from "react";
import {
  TbAdjustmentsHorizontal,
  TbArrowLeft,
  TbArrowRight,
  TbDeviceFloppy,
  TbListCheck,
  TbLock,
  TbX,
} from "react-icons/tb";
import { ApiService } from "api/api.service";
import { ApplicationDto } from "dtos/application.dto";
import { StrategyConfigV2 } from "dtos/strategy.dto";
import { queryClient } from "utils/queryClient";
import { buildBaseStrategy, derivePurpose, StrategyFormValues, toStrategyConfig, toWizardValues } from "./functions";
import StrategyPreviewSections, { PREVIEW_SECTION_LABELS, PreviewSection } from "./StrategyPreviewSections";
import PurposeStep from "./wizard/PurposeStep";
import PaymentsStep from "./wizard/PaymentsStep";
import CommissionStep from "./wizard/CommissionStep";
import DetailsStep from "./wizard/DetailsStep";
import AdvancedEditor, { AdvancedFormValues } from "./wizard/AdvancedEditor";

type WizardStep = {
  title: string;
  description: string;
  preview: PreviewSection[];
  /** Top level form names validated before leaving the step. */
  fields: string[];
  nextHint?: string;
};

const STEPS: WizardStep[] = [
  {
    title: "Purpose",
    description: "",
    preview: ["metrics"],
    fields: ["purpose"],
    nextHint: "Next: the strategy overview fills in once you set payments & fees.",
  },
  {
    title: "Payments & fees",
    description: "",
    preview: ["overview"],
    fields: [
      "payment_type",
      "target_weekly_calculation_strategy",
      "initial_fee",
      "initial_bias_amount",
      "interest_calculation_strategy",
      "program_fee",
      "success_fee",
      "interest_amount",
      "dynamic_weekly_pushback",
    ],
    nextHint: "Next: payment breakdown and timeline appear when you choose how commission is collected.",
  },
  {
    title: "Commission",
    description: "",
    preview: ["breakdown", "timeline"],
    fields: ["program_split"],
    nextHint: "The last step shows the full preview, including the ledger.",
  },
  {
    title: "Creditors & details",
    description: "",
    preview: ["metrics", "overview", "breakdown", "timeline", "ledger"],
    fields: ["creditor_split", "key", "description", "template_id"],
  },
];

const WizardBody = ({
  application,
  orgCode,
  appkey,
  savedStrategies,
  editIndex,
  onClose,
  onSaved,
}: {
  application: ApplicationDto;
  orgCode?: string;
  appkey: string;
  savedStrategies: StrategyConfigV2[];
  editIndex?: number;
  onClose: () => void;
  onSaved?: (index: number) => void;
}) => {
  const form = Form.useFormInstance<StrategyFormValues>();
  const isEdit = editIndex != null;
  const [step, setStep] = useState(0);
  const [visited, setVisited] = useState(isEdit ? STEPS.length - 1 : 0);
  /** Snapshot handed to the advanced (old) editor; set while advanced mode is on. */
  const [advancedValues, setAdvancedValues] = useState<StrategyFormValues>();
  const advancedFormRef = useRef<FormInstance<AdvancedFormValues>>();
  const isAdvanced = Boolean(advancedValues);
  const saveCustomStrategy = ApiService.application.useSaveCustomStrategiesV2();

  const values = Form.useWatch((all) => all, { form, preserve: true }) as StrategyFormValues | undefined;
  const config = useMemo(() => (values ? toStrategyConfig(values) : undefined), [values]);
  const current = STEPS[step];

  const goTo = (index: number) => {
    setStep(index);
    setVisited((prev) => Math.max(prev, index));
  };

  const next = async () => {
    try {
      await form.validateFields(current.fields, { recursive: true });
      goTo(step + 1);
    } catch {
      message.error("Please fix the highlighted fields.");
    }
  };

  const openAdvanced = () => setAdvancedValues(form.getFieldsValue(true));

  /** Copies the advanced editor's values back into the guided form. */
  const syncFromAdvanced = () => {
    const edited = advancedFormRef.current?.getFieldValue(["strategies", 0]) as StrategyFormValues | undefined;
    if (edited)
      form.setFieldsValue({ ...edited, purpose: derivePurpose(edited) } as Parameters<typeof form.setFieldsValue>[0]);
  };

  const closeAdvanced = () => {
    syncFromAdvanced();
    setAdvancedValues(undefined);
  };

  const save = async () => {
    if (isAdvanced) {
      try {
        await advancedFormRef.current?.validateFields();
      } catch {
        message.error("Please fix the highlighted fields.");
        return;
      }
      syncFromAdvanced();
    }

    try {
      await form.validateFields();
    } catch (error) {
      const first = (error as { errorFields?: { name: (string | number)[] }[] }).errorFields?.[0]?.name?.[0];
      const index = STEPS.findIndex((s) => s.fields.includes(String(first)));
      if (isAdvanced) closeAdvanced();
      if (index >= 0) setStep(index);
      message.error("Please fix the highlighted fields.");
      return;
    }

    const strategy = toStrategyConfig(form.getFieldsValue(true));
    const body = [...savedStrategies];
    if (isEdit) body[editIndex] = strategy;
    else body.push(strategy);

    saveCustomStrategy.mutate(
      { appkey, body },
      {
        onSuccess: async () => {
          message.success(isEdit ? "Strategy updated." : "Strategy saved.");
          queryClient.invalidateQueries({ queryKey: ["application"] });
          await queryClient.refetchQueries({ queryKey: ["get-strategy"] });
          onSaved?.(isEdit ? editIndex : body.length - 1);
          onClose();
        },
      },
    );
  };

  return (
    <div className="flex flex-col h-[min(800px,86vh)]">
      {/* Header */}
      <div className="px-7 pt-5">
        <div className="flex items-start justify-between gap-4">
          <div>
            <div className="flex items-center gap-2.5">
              <Typography.Title level={4} className="!m-0">
                {isEdit ? "Edit custom strategy" : "New custom strategy"}
              </Typography.Title>
              <Tag color="purple" icon={<TbLock className="mr-1 -mb-0.5" />} className="!rounded-full !m-0">
                Manager approval
              </Tag>
            </div>
            <Typography.Text className="!text-[13px] !text-gray-600">
              {isAdvanced
                ? "Full editor with every option. Changes carry over to the guided setup."
                : "Answer a few questions — the preview on the right updates as you go."}
            </Typography.Text>
          </div>
          <div className="flex items-center gap-2">
            {isAdvanced ? (
              <Button icon={<TbListCheck />} color="purple" variant="outlined" onClick={closeAdvanced}>
                Back to guided setup
              </Button>
            ) : (
              <Button icon={<TbAdjustmentsHorizontal />} onClick={openAdvanced}>
                Advanced settings
              </Button>
            )}
            <Button type="text" aria-label="Close" icon={<TbX size={18} />} onClick={onClose} />
          </div>
        </div>
        <Steps
          size="small"
          className={`!py-4 border-0 border-b border-solid border-gray-200 ${isAdvanced ? "!hidden" : ""}`}
          current={step}
          onChange={(index) => index <= visited && setStep(index)}
          items={STEPS.map((s, i) => ({ title: s.title, description: s.description, disabled: i > visited }))}
        />
      </div>

      {isAdvanced && advancedValues && (
        <div className="grow flex flex-col min-h-0 border-0 border-t border-solid border-gray-200">
          <div className="grow overflow-y-auto px-6 py-4">
            <AdvancedEditor application={application} initialValues={advancedValues} formRef={advancedFormRef} />
          </div>
          <div className="flex items-center justify-between px-7 py-3.5 border-0 border-t border-solid border-gray-200">
            <Button icon={<TbListCheck />} onClick={closeAdvanced}>
              Back to guided setup
            </Button>
            <Button type="primary" icon={<TbDeviceFloppy />} loading={saveCustomStrategy.isPending} onClick={save}>
              {isEdit ? "Save changes" : "Save strategy"}
            </Button>
          </div>
        </div>
      )}

      {/* Body — kept mounted (hidden) in advanced mode so the guided fields stay registered. */}
      <div className={`grow grid grid-cols-2 min-h-0 ${isAdvanced ? "!hidden" : ""}`}>
        <div className="relative flex flex-col min-h-0 border-0 border-r border-solid border-gray-200">
          <div className="grow overflow-y-auto px-7 py-6">
            <div className={step === 0 ? "" : "hidden"}>
              <PurposeStep />
            </div>
            <div className={step === 1 ? "" : "hidden"}>
              <PaymentsStep />
            </div>
            <div className={step === 2 ? "" : "hidden"}>
              <CommissionStep totalDebt={application.enrolled_debt ?? 0} />
            </div>
            <div className={step === 3 ? "" : "hidden"}>
              <DetailsStep orgCode={orgCode} />
            </div>
          </div>

          <div className="flex items-center justify-between px-7 py-3.5 border-0 border-t border-solid border-gray-200">
            <Button icon={<TbArrowLeft />} disabled={step === 0} onClick={() => setStep(step - 1)}>
              Prev
            </Button>
            <Typography.Text className="!text-[13px] !text-gray-500">
              Step {step + 1} of {STEPS.length}
            </Typography.Text>
            {step < STEPS.length - 1 ? (
              <Button type="primary" icon={<TbArrowRight />} iconPosition="end" onClick={next}>
                Next
              </Button>
            ) : (
              <Button type="primary" icon={<TbDeviceFloppy />} loading={saveCustomStrategy.isPending} onClick={save}>
                {isEdit ? "Save changes" : "Save strategy"}
              </Button>
            )}
          </div>
        </div>

        {/* Preview */}
        <div className="flex flex-col min-h-0 bg-[#f7f7fa]">
          <div className="flex items-center justify-between px-6 pt-4">
            <div className="flex items-center gap-2">
              <span className="w-2 h-2 rounded-full bg-emerald-500" />
              <span className="text-xs font-semibold uppercase tracking-wider text-gray-600">Live preview</span>
            </div>
            <div className="flex gap-1.5">
              {(Object.keys(PREVIEW_SECTION_LABELS) as PreviewSection[]).map((section) => (
                <span
                  key={section}
                  className={`text-[11px] px-2 py-0.5 rounded-full ${
                    current.preview.includes(section)
                      ? "bg-violet-100 text-violet-800 font-semibold"
                      : "bg-gray-200/70 text-gray-500"
                  }`}
                >
                  {PREVIEW_SECTION_LABELS[section]}
                </span>
              ))}
            </div>
          </div>
          <div className="grow overflow-y-auto px-6 pt-8 pb-6 flex flex-col gap-3.5">
            {config && (
              <StrategyPreviewSections
                application={application}
                config={config}
                sections={current.preview}
                onWeeklyBudgetChange={(value) => form.setFieldValue(["meta", "weekly_budget"], value)}
              />
            )}
            {current.nextHint && (
              <div className="border border-dashed border-gray-300 rounded-xl px-4 py-3.5 text-[13px] text-gray-500 text-center">
                {current.nextHint}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

/**
 * Guided, one-strategy-at-a-time editor for V2 custom strategies.
 * - `editIndex` edits a saved strategy.
 * - `basePlanSlug` either opens the saved strategy with that slug, or starts a new one from the org plan.
 */
const StrategyWizardModal = ({
  open,
  appkey,
  orgCode,
  basePlanSlug,
  editIndex,
  onClose,
  onSaved,
}: {
  open: boolean;
  appkey: string | undefined;
  orgCode: string;
  basePlanSlug?: string;
  editIndex?: number;
  onClose: () => void;
  onSaved?: (index: number) => void;
}) => {
  const [form] = Form.useForm<StrategyFormValues>();
  const [initialValues, setInitialValues] = useState<StrategyFormValues>();

  const applicationResponse = ApiService.application.useGetByAppKey(appkey);
  const getCustomStrategy = ApiService.application.useGetCustomStrategies(appkey);
  const getAllStrategyConfigs = ApiService.strategy.useGetAllStrategyConfigs({ orgCode });

  const application = applicationResponse.data?.application as ApplicationDto | undefined;
  const savedStrategies = useMemo(() => getCustomStrategy.data?.result.v2 ?? [], [getCustomStrategy.data]);

  const resolvedEditIndex = useMemo(() => {
    if (editIndex != null) return editIndex;
    if (!basePlanSlug) return undefined;
    const index = savedStrategies.findIndex((strategy) => strategy.slug === basePlanSlug);
    return index >= 0 ? index : undefined;
  }, [editIndex, basePlanSlug, savedStrategies]);

  const ready =
    Boolean(application) && getCustomStrategy.isFetched && (!basePlanSlug || getAllStrategyConfigs.isFetched);

  // Snapshot the starting values once per opening, so refetches don't reset the form mid-edit.
  useEffect(() => {
    if (!open) {
      setInitialValues(undefined);
      return;
    }
    if (!ready || initialValues) return;

    if (resolvedEditIndex != null && savedStrategies[resolvedEditIndex]) {
      setInitialValues(toWizardValues(savedStrategies[resolvedEditIndex]));
    } else {
      const basePlan = getAllStrategyConfigs.data?.find((config) => config.slug === basePlanSlug);
      setInitialValues(buildBaseStrategy(basePlan, `Custom Strategy ${savedStrategies.length + 1}`));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- only (re)initialise when opening or once data is ready
  }, [open, ready, initialValues, resolvedEditIndex]);

  return (
    <Modal
      open={open}
      onCancel={onClose}
      footer={null}
      closable={false}
      maskClosable={false}
      centered
      width={1200}
      zIndex={1010}
      destroyOnHidden
      styles={{ content: { padding: 0, overflow: "hidden" } }}
    >
      {application && initialValues ? (
        <Form
          form={form}
          initialValues={initialValues}
          layout="vertical"
          autoComplete="off"
          name="custom_strategy_wizard"
        >
          <WizardBody
            application={application}
            orgCode={application.organisation?.code ?? orgCode}
            appkey={appkey!}
            savedStrategies={savedStrategies}
            editIndex={resolvedEditIndex}
            onClose={onClose}
            onSaved={onSaved}
          />
        </Form>
      ) : (
        <div className="flex items-center justify-center h-[60vh]">
          <Spin />
        </div>
      )}
    </Modal>
  );
};

export default StrategyWizardModal;
